import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { geoAlbers } from 'd3-geo';
// topojson-client ships no types and has no @types package; require() avoids a separate ambient .d.ts file
const { feature } = require('topojson-client');
import type { Feature, FeatureCollection } from 'geojson';
import type { Topology } from 'topojson-specification';
import { CANADA_REGION_CODES } from '@trnsact/trnsact-shared-types';
import usStatesTopology from 'us-atlas/states-10m.json';
import canadaProvincesTopology from 'assets/geo/canada-provinces-topo.json';
import { LenderRetailActivityStateRowViewModel } from '../../../api/types';
import { CREDIT_APPS_BUCKET_LEGEND, getCreditAppsBucketColor, mapCreditAppsByStateCode } from '../../../lib';

interface Props {
  rows: LenderRetailActivityStateRowViewModel[];
  loading?: boolean;
}

const MAP_WIDTH = 700;
const MAP_HEIGHT = 600;
const DEFAULT_FILL = blueGrey[50];
const BORDER_COLOR = '#757575';

// Hawaii/Pacific & Caribbean territories sit far outside the US mainland + Canada extent
// (Guam/N. Mariana are ~145°E — literally the opposite side of the globe), which breaks a single
// shared conic projection (this is why geoAlbersUsa insets them and drops the rest). Alaska is kept
// but needs its antimeridian-crossing coordinates unwrapped (see fixAntimeridianCrossing below).
const US_MAP_EXCLUDED_FIPS = new Set(['15', '60', '66', '69', '78']);

// us-atlas geometries are keyed by 2-digit FIPS state code, not USPS code
const US_FIPS_TO_CODE: Record<string, string> = {
  '01': 'AL',
  '02': 'AK',
  '04': 'AZ',
  '05': 'AR',
  '06': 'CA',
  '08': 'CO',
  '09': 'CT',
  '10': 'DE',
  '11': 'DC',
  '12': 'FL',
  '13': 'GA',
  '15': 'HI',
  '16': 'ID',
  '17': 'IL',
  '18': 'IN',
  '19': 'IA',
  '20': 'KS',
  '21': 'KY',
  '22': 'LA',
  '23': 'ME',
  '24': 'MD',
  '25': 'MA',
  '26': 'MI',
  '27': 'MN',
  '28': 'MS',
  '29': 'MO',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '33': 'NH',
  '34': 'NJ',
  '35': 'NM',
  '36': 'NY',
  '37': 'NC',
  '38': 'ND',
  '39': 'OH',
  '40': 'OK',
  '41': 'OR',
  '42': 'PA',
  '44': 'RI',
  '45': 'SC',
  '46': 'SD',
  '47': 'TN',
  '48': 'TX',
  '49': 'UT',
  '50': 'VT',
  '51': 'VA',
  '53': 'WA',
  '54': 'WV',
  '55': 'WI',
  '56': 'WY',
  '72': 'PR',
};

// Full province name (as used by the canada.geojson source) -> 2-letter code
const CANADA_NAME_TO_CODE: Record<string, string> = {
  Alberta: CANADA_REGION_CODES.Alberta,
  'British Columbia': CANADA_REGION_CODES.BritishColumbia,
  Manitoba: CANADA_REGION_CODES.Manitoba,
  'New Brunswick': CANADA_REGION_CODES.NewBrunswick,
  'Newfoundland and Labrador': CANADA_REGION_CODES.NewfoundlandAndLabrador,
  'Nova Scotia': CANADA_REGION_CODES.NovaScotia,
  'Northwest Territories': CANADA_REGION_CODES.NorthwestTerritories,
  Nunavut: CANADA_REGION_CODES.Nunavut,
  Ontario: CANADA_REGION_CODES.Ontario,
  'Prince Edward Island': CANADA_REGION_CODES.PrinceEdwardIsland,
  Quebec: CANADA_REGION_CODES.Quebec,
  Saskatchewan: CANADA_REGION_CODES.Saskatchewan,
  'Yukon Territory': CANADA_REGION_CODES.Yukon,
};

// Alaska's westernmost Aleutian islands are recorded at positive longitude (~172-179°E) instead of
// continuing past -180°, which makes them numerically ~351° away from the rest of Alaska/North
// America and breaks a shared conic projection's fit. Shifting positive longitudes by -360° makes
// the whole feature numerically continuous again (e.g. 172°E becomes -188°).
const fixAntimeridianCrossing = (geo: Feature): Feature => {
  const shiftRing = (ring: number[][]) => ring.map(([lon, lat]) => [lon > 0 ? lon - 360 : lon, lat]);
  const { geometry } = geo;

  if (geometry.type === 'Polygon') {
    return { ...geo, geometry: { ...geometry, coordinates: geometry.coordinates.map(shiftRing) } };
  }
  if (geometry.type === 'MultiPolygon') {
    return {
      ...geo,
      geometry: { ...geometry, coordinates: geometry.coordinates.map(polygon => polygon.map(shiftRing)) },
    };
  }
  return geo;
};

export const GeographicBreakdownMap = ({ rows, loading }: Props) => {
  const creditAppsByCode = useMemo(() => mapCreditAppsByStateCode(rows), [rows]);
  const hasData = rows.some(row => row.creditApps > 0);

  const mainUsFeatures = useMemo(() => {
    const topology = usStatesTopology as unknown as Topology;
    const collection = feature(topology, topology.objects.states) as FeatureCollection;
    return collection.features
      .filter((geo: Feature) => !US_MAP_EXCLUDED_FIPS.has(String(geo.id)))
      .map((geo: Feature) => (geo.id === '02' ? fixAntimeridianCrossing(geo) : geo));
  }, []);

  const mainCanadaFeatures = useMemo(() => {
    const topology = canadaProvincesTopology as unknown as Topology;
    return (feature(topology, topology.objects.provinces) as FeatureCollection).features;
  }, []);

  // Single projection fitted to the combined US (excluding AK/HI/territories) + full Canada extent
  const combinedFeatures = useMemo(
    () => [...mainUsFeatures, ...mainCanadaFeatures],
    [mainUsFeatures, mainCanadaFeatures]
  );

  const projection = useMemo(
    () =>
      geoAlbers()
        .rotate([96, 0])
        .parallels([45, 65])
        // fitSize must trace the real shapes, not a lon/lat bounding rectangle — a conic projection
        // distorts a rectangle's corners far more than the actual coastline, shrinking the fit
        .fitSize([MAP_WIDTH, MAP_HEIGHT], { type: 'FeatureCollection', features: combinedFeatures } as any),
    [combinedFeatures]
  );

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2" component="span">
        Geographic breakdown
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        {loading ? (
          <Skeleton variant="rounded" height={MAP_HEIGHT} />
        ) : !hasData ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            No data
          </Typography>
        ) : (
          <Stack gap={2}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2}>
              {CREDIT_APPS_BUCKET_LEGEND.map(item => (
                <Stack key={item.label} direction="row" alignItems="center" gap={1}>
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: item.color, flexShrink: 0 }} />
                  <Typography variant="body2">{item.label}</Typography>
                </Stack>
              ))}
            </Stack>

            <ComposableMap
              width={MAP_WIDTH}
              height={MAP_HEIGHT}
              projection={projection}
              // ComposableMap only sets a viewBox, not a CSS size, so without this it's left to the
              // browser/flexbox default size and isn't centered in its container
              style={{ display: 'block', width: '100%', maxWidth: MAP_WIDTH, height: 'auto', margin: '0 auto' }}
            >
              <Geographies geography={mainUsFeatures}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const code = US_FIPS_TO_CODE[String(geo.id)];
                    const creditApps = code ? (creditAppsByCode[code] ?? 0) : 0;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getCreditAppsBucketColor(creditApps) ?? DEFAULT_FILL}
                        stroke={BORDER_COLOR}
                        strokeWidth={0.5}
                      >
                        <title>{`${geo.properties?.name}: ${creditApps} apps`}</title>
                      </Geography>
                    );
                  })
                }
              </Geographies>

              <Geographies geography={mainCanadaFeatures}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const code = CANADA_NAME_TO_CODE[String(geo.properties?.name)];
                    const creditApps = code ? (creditAppsByCode[code] ?? 0) : 0;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getCreditAppsBucketColor(creditApps) ?? DEFAULT_FILL}
                        stroke={BORDER_COLOR}
                        strokeWidth={0.5}
                      >
                        <title>{`${geo.properties?.name}: ${creditApps} apps`}</title>
                      </Geography>
                    );
                  })
                }
              </Geographies>

              <Marker coordinates={[-98, 40]}>
                <text
                  textAnchor="middle"
                  style={{ fontSize: 16, fontWeight: 600, /* fill: BORDER_COLOR, */ pointerEvents: 'none' }}
                >
                  UNITED STATES
                </text>
              </Marker>
              <Marker coordinates={[-102, 55]}>
                <text
                  textAnchor="middle"
                  style={{ fontSize: 16, fontWeight: 600, /* fill: BORDER_COLOR, */ pointerEvents: 'none' }}
                >
                  CANADA
                </text>
              </Marker>
            </ComposableMap>
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
